/**
 * Schedule time helpers: 12-hour entry with optional AM/PM period.
 */

export function normalizeSchedulePeriod(raw) {
  const p = String(raw || '').trim().toUpperCase();
  if (p === 'AM' || p === 'PM') return p;
  return 'AM';
}

/** Strip to digits and colon for live typing. */
export function formatTimeInputMask(raw) {
  const s = String(raw || '').replace(/[^\d:]/g, '');
  if (!s) return '';
  const digits = s.replace(/:/g, '');
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) {
    return `${digits.slice(0, digits.length - 2)}:${digits.slice(-2)}`;
  }
  return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
}

/**
 * @param {string} time e.g. "10:30"
 * @param {'AM'|'PM'} period
 * @returns {string} display e.g. "10:30 AM"
 */
export function formatScheduleTimeDisplay(time, period = 'AM') {
  const t = String(time || '').trim();
  const p = normalizeSchedulePeriod(period);
  if (!t) return '';
  return `${t} ${p}`;
}

/**
 * Parse stored time + period from legacy single time string.
 * @returns {{ time: string, period: 'AM'|'PM' }}
 */
export function parseScheduleTimeFields(timeRaw, periodRaw) {
  if (periodRaw) {
    return {
      time: formatTimeInputMask(timeRaw),
      period: normalizeSchedulePeriod(periodRaw),
    };
  }
  const s = String(timeRaw || '').trim();
  const m = s.match(/^(\d{1,2}(?::\d{2})?)\s*(AM|PM)?$/i);
  if (m) {
    return {
      time: formatTimeInputMask(m[1]),
      period: normalizeSchedulePeriod(m[2] || 'AM'),
    };
  }
  return { time: formatTimeInputMask(s), period: 'AM' };
}

/**
 * @param {string} time
 * @param {'AM'|'PM'} period
 * @returns {number|null} minutes from midnight
 */
export function scheduleTimeToMinutes(time, period = 'AM') {
  const { time: t, period: p } = parseScheduleTimeFields(time, period);
  const parts = t.split(':');
  if (!parts[0]) return null;
  let h = Number(parts[0]);
  let m = Number(parts[1] || 0);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  if (h > 12) {
    return h * 60 + m;
  }
  const per = normalizeSchedulePeriod(p);
  if (h === 12) h = per === 'AM' ? 0 : 12;
  else if (per === 'PM') h += 12;
  return h * 60 + m;
}

/** Compare two schedule line objects by call time (null times sort last). */
export function compareScheduleLinesByTime(a, b) {
  const ma = scheduleTimeToMinutes(a?.time, a?.period);
  const mb = scheduleTimeToMinutes(b?.time, b?.period);
  if (ma == null && mb == null) return 0;
  if (ma == null) return 1;
  if (mb == null) return -1;
  return ma - mb;
}

/**
 * Re-order schedule/unset rows chronologically while text and blank rows stay put.
 * @param {Array<{ kind: string, time?: string, period?: string }>} lines
 */
export function sortScheduleLinesChronologically(lines) {
  if (!Array.isArray(lines) || lines.length < 2) return lines;

  const scheduleLines = [];
  const scheduleSlotIndexes = [];
  lines.forEach((ln, index) => {
    if (ln.kind === 'schedule' || ln.kind === 'unset') {
      scheduleSlotIndexes.push(index);
      scheduleLines.push(ln);
    }
  });
  if (scheduleLines.length < 2) return lines;

  const sorted = [...scheduleLines].sort(compareScheduleLinesByTime);
  const next = [...lines];
  scheduleSlotIndexes.forEach((lineIndex, i) => {
    next[lineIndex] = sorted[i];
  });
  return next;
}
